import { useSyncExternalStore } from 'react';
import { FileText } from '../../../icons';
import type { ChatMessageEvent, MeetingSummaryPayload } from '../../../types/chat';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';

interface Props {
  message: ChatMessageEvent;
}

/**
 * MeetingSummaryCard (P2-16g) — AI 회의 요약 카드.
 *
 * Phase 5 ai-gateway 가 회의록 요약 발화 시 본 카드로 렌더. 시안에 직접 mock 없으므로
 * 일반적 회의 요약 포맷 — title / 일시 / 참석자 / 안건 / 핵심 결정 / 액션 아이템.
 *
 * `aiActionData.payload.kind !== 'meeting_summary'` 또는 부재 시 fallback.
 */
export function MeetingSummaryCard({ message }: Props) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  const data = message.aiActionData;
  if (!data || !isMeetingSummary(data.payload)) return <Fallback message={message} />;
  return <Body payload={data.payload} />;
}

function Body({ payload }: { payload: MeetingSummaryPayload }) {
  return (
    <div className="chat-ai-action-card chat-ai-action-form" role="region" aria-label={tChat('chat.meeting.aria')}>
      <header className="chat-ai-action-card-header">
        <div className="chat-ai-action-card-icon">
          <FileText size={16} />
        </div>
        <div className="chat-ai-action-card-title-block">
          <div className="chat-ai-action-card-title">{payload.title}</div>
          <div className="chat-ai-action-card-subtitle">
            {payload.datetime}
            {payload.durationLabel && ` · ${payload.durationLabel}`}
          </div>
        </div>
        <span className="chat-ai-action-card-badge">{tChat('chat.meeting.badge')}</span>
      </header>

      <div className="chat-ai-action-form-grid">
        <Section label={tChat('chat.meeting.label_attendees')} value={payload.attendees.join(', ')} />
        {payload.agenda && payload.agenda.length > 0 && <List label={tChat('chat.meeting.label_agenda')} items={payload.agenda} />}
        {payload.keyDecisions && payload.keyDecisions.length > 0 && <List label={tChat('chat.meeting.label_key_decisions')} items={payload.keyDecisions} />}
        {payload.actionItems && payload.actionItems.length > 0 && (
          <div className="chat-ai-action-form-row">
            <div className="chat-ai-action-form-row-label">{tChat('chat.meeting.label_action_items')}</div>
            <ul className="chat-ai-action-form-list">
              {payload.actionItems.map((it, i) => (
                <li key={i}>
                  <span className="chat-ai-action-form-list-owner">{it.owner}</span>
                  {' — '}{it.task}
                  {it.due && <span className="chat-ai-action-form-list-due"> ({it.due})</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ label, value }: { label: string; value: string }) {
  return (
    <div className="chat-ai-action-form-row">
      <div className="chat-ai-action-form-row-label">{label}</div>
      <div className="chat-ai-action-form-row-value">{value}</div>
    </div>
  );
}

function List({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="chat-ai-action-form-row">
      <div className="chat-ai-action-form-row-label">{label}</div>
      <ul className="chat-ai-action-form-list">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  );
}

function isMeetingSummary(p: unknown): p is MeetingSummaryPayload {
  return typeof p === 'object' && p !== null && (p as { kind?: unknown }).kind === 'meeting_summary';
}

function Fallback({ message }: { message: ChatMessageEvent }) {
  return (
    <div className="chat-ai-action-card chat-ai-action-card--unknown" role="region" aria-label={tChat('chat.meeting.aria')}>
      <header className="chat-ai-action-card-header">
        <div className="chat-ai-action-card-icon">
          <FileText size={16} />
        </div>
        <div className="chat-ai-action-card-title-block">
          <div className="chat-ai-action-card-title">{tChat('chat.meeting.fallback_title')}</div>
          <div className="chat-ai-action-card-subtitle">{tChat('chat.meeting.fallback_subtitle')}</div>
        </div>
      </header>
      <div className="chat-ai-action-card-body">
        <div className="chat-ai-action-card-content">{message.content}</div>
      </div>
    </div>
  );
}
